import { FirebaseAuthTypes, signOut as signOutMobile } from '@react-native-firebase/auth';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { Auth, signOut as signOutWeb } from 'firebase/auth';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetAllShows, useGetInfluencerDiscount, usePurchaseShow } from '@/api/hooks/showHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import TokneizedACHView from '@/components/paymentMethods/TokenizedACHView';
import TokenizedCardView from '@/components/paymentMethods/TokenizedCardView';
import Text from '@/components/Text';
import { APPSFLYER_CONFIG } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { auth } from '@/firebaseConfig';
import { applyDiscount } from '@/utils/applyDiscount';
import { AppStorage } from '@/utils/AppStorage';
import { decryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import { TokenizedACHData } from '../paymentMethods/achList';
import { TokenizedCreditCardData } from '../paymentMethods/creditCardList';

const ConfirmPurchaseShow = () => {
  const navigation = useNavigation();

  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const { dispatch, clearCurrentUser } = useAuth();
  const { clearFavorites } = useMeFavorites();

  const [payloadState, setPayloadState] = useState<{
    tokenizedCard?: string;
    tokenizedACH?: string;
    fromIOS?: string;
    showId?: string;
    influencerCode?: string;
  }>();

  const { data: showData, isPending: allShowsPending } = useGetAllShows();
  const { data: influencerDiscount, isPending: influencerDiscountPending } = useGetInfluencerDiscount({
    influencerCode: payloadState?.influencerCode ?? ''
  });

  const { mutateAsync: purchaseShow, isPending } = usePurchaseShow();
  const { showSuccess } = useEndFlowModal();

  const [parsedTokenizedCard, setParsedTokenizedCard] = useState<TokenizedCreditCardData>();
  const [parsedTokenizedACH, setParsedTokenizedACH] = useState<TokenizedACHData>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        tokenizedCard?: string;
        tokenizedACH?: string;
        fromIOS?: string;
        showId?: string;
        influencerCode?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        setParsedTokenizedCard(safeJsonParse<TokenizedCreditCardData>(payloadObject?.tokenizedCard ?? ''));
        setParsedTokenizedACH(safeJsonParse<TokenizedACHData>(payloadObject?.tokenizedACH ?? ''));
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useLayoutEffect(() => {
    if (payloadState?.fromIOS) {
      navigation.setOptions({
        headerLeft: () => (
          <GoBackButton
            onPress={() => {
              dispatch({ type: 'SIGN_OUT' });
              clearCurrentUser();
              if (Platform.OS === 'web') {
                signOutWeb(auth() as Auth);
              } else {
                signOutMobile(auth() as FirebaseAuthTypes.Module);
              }
              clearFavorites();
              const params = new URLSearchParams({
                deep_link_value: 'purchaseShow',
                dismissBrowser: 'true'
              });
              const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
              Linking.openURL(backRoute);
            }}
          />
        )
      });
    }
  }, [clearCurrentUser, clearFavorites, dispatch, payloadState?.fromIOS, navigation]);

  const handleConfirm = () => {
    try {
      purchaseShow(
        {
          key: parsedTokenizedCard?.card_key ?? parsedTokenizedACH?.check_key ?? '',
          showId: payloadState?.showId ?? '',
          paymentType: parsedTokenizedCard?.card_key !== undefined ? 'CREDIT_CARD' : 'ACH',
          code: payloadState?.influencerCode ?? undefined
        },
        {
          onSuccess: (_) => {
            if (!payloadState?.fromIOS) {
              AppStorage.deleteItem('influencerCode');
              showSuccess({
                title: i18n.t('trb_show.purchase_succes_title'),
                message: i18n.t('trb_show.purchase_succes_message')
              });
              router.dismissAll();
              router.push('/home/npoTabs');
              router.navigate({
                pathname: '/purchaseShow'
              });
            } else {
              showSuccess({
                title: i18n.t('trb_show.purchase_succes_title'),
                message: i18n.t('trb_show.purchase_succes_message'),
                onButtonPress: () => {
                  dispatch({ type: 'SIGN_OUT' });
                  clearCurrentUser();
                  if (Platform.OS === 'web') {
                    signOutWeb(auth() as Auth);
                  } else {
                    signOutMobile(auth() as FirebaseAuthTypes.Module);
                  }
                  clearFavorites();
                  const params = new URLSearchParams({
                    deep_link_value: 'purchaseShow?clearInfluencerCode=true',
                    dismissBrowser: 'true'
                  });
                  const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
                  Linking.openURL(backRoute);
                }
              });
            }
          }
        }
      );
    } catch (e) {
      console.error(e);
      if (payloadState?.fromIOS) {
        dispatch({ type: 'SIGN_OUT' });
        clearCurrentUser();
        if (Platform.OS === 'web') {
          signOutWeb(auth() as Auth);
        } else {
          signOutMobile(auth() as FirebaseAuthTypes.Module);
        }
        clearFavorites();
        const params = new URLSearchParams({
          deep_link_value: 'purchaseShow',
          dismissBrowser: 'true'
        });
        const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
        Linking.openURL(backRoute);
      }
    }
  };

  if (allShowsPending) {
    return (
      <SafeAreaView style={[styles.flexOne, { justifyContent: 'center', alignItems: 'center' }]} edges={['bottom']}>
        <DotsLoader />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <Text style={styles.title} fontWeight="700">
          {i18n.t('trb_show.about_to_purchase')}
        </Text>
        <Text fontWeight="900" style={styles.showTitle}>
          {showData?.title}
        </Text>
        <Text>{showData?.description}</Text>
        <Text
          fontWeight="700"
          style={{
            ...(influencerDiscount &&
              !influencerDiscountPending &&
              influencerDiscount.discountPercent > 0 &&
              showData &&
              showData.bsAppPrice &&
              applyDiscount(parseFloat(showData.bsAppPrice), influencerDiscount.discountPercent) !==
                showData.bsAppPrice && {
                textDecorationLine: 'line-through'
              })
          }}>
          {i18n.t('trb_show.normal_price', { PRICE: showData?.bsAppPrice })}
        </Text>
        {influencerDiscount &&
          !influencerDiscountPending &&
          influencerDiscount.discountPercent > 0 &&
          showData &&
          showData.bsAppPrice &&
          applyDiscount(parseFloat(showData.bsAppPrice), influencerDiscount.discountPercent) !==
            showData.bsAppPrice && (
            <Text fontWeight="900" style={styles.specialPrice}>
              {i18n.t('trb_show.special_price', {
                PRICE: applyDiscount(parseFloat(showData.bsAppPrice), influencerDiscount.discountPercent)
              })}
            </Text>
          )}
        <Text style={styles.taxes}>{i18n.t('trb_show.taxes_included')}</Text>
        {/* Payment Method */}
        <View style={styles.dataRow}>
          <Text style={styles.title} fontWeight="700">
            {i18n.t('donation_summary.payment_method')}
          </Text>
        </View>

        {parsedTokenizedCard && <TokenizedCardView tokenizedCard={parsedTokenizedCard} showDelete={false} />}
        {parsedTokenizedACH && <TokneizedACHView tokenizedAch={parsedTokenizedACH} showDelete={false} />}

        <BrandedButton onPress={handleConfirm} loading={isPending}>
          {i18n.t('mix.confirm')}
        </BrandedButton>
      </View>
    </SafeAreaView>
  );
};

export default ConfirmPurchaseShow;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0,
    paddingVertical: 18,
    gap: 15
  },

  title: {
    fontSize: 24,
    color: 'black'
  },

  showTitle: {
    fontSize: 28,
    color: 'black'
  },

  dataRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center'
  },

  data: {
    fontSize: 18,
    color: 'black'
  },

  specialPrice: {
    color: 'green',
    fontSize: 15
  },

  taxes: {
    fontSize: 13
  },

  checkContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  checkLabel: {
    fontSize: 14,
    color: 'black',
    fontWeight: '400'
  }
});
